/**
 * Loglama yardımcı sınıfı
 * Uygulamanın farklı yerlerinde loglama işlemlerini standardize etmek için kullanılır
 */

// Log tipleri ve karşılık gelen renkler/ikonlar için sabitler
export const LOG_TYPES = {
    info: {
        color: 'info.main',
        label: 'BİLGİ',
        consoleMethod: 'log',
    },
    success: {
        color: 'success.main',
        label: 'BAŞARILI',
        consoleMethod: 'log',
    },
    warning: {
        color: 'warning.main',
        label: 'UYARI',
        consoleMethod: 'warn',
    },
    error: {
        color: 'error.main',
        label: 'HATA',
        consoleMethod: 'error',
    },
    debug: {
        color: 'text.secondary',
        label: 'DEBUG',
        consoleMethod: 'debug',
    },
};

/**
 * Logger sınıfı - loglama işlemlerini merkezi olarak yönetir
 */
class Logger {
    constructor() {
        this._logs = [];
        this._subscribers = [];
        this._maxLogs = 500; // Maksimum tutulacak log sayısı
    }

    /**
     * Log ekle
     * @param {string} type - Log tipi: 'info', 'success', 'warning', 'error', 'debug'
     * @param {string} message - Log mesajı
     * @param {Object} [data] - İlave veri (opsiyonel)
     */
    add(type, message, data = null) {
        if (!Object.keys(LOG_TYPES).includes(type)) {
            type = 'info'; // Varsayılan tip
        }

        const timestamp = new Date().toLocaleTimeString();

        // Konsolda logla
        console[LOG_TYPES[type].consoleMethod](`[${LOG_TYPES[type].label}] ${message}`, data || '');

        // Log nesnesini oluştur
        const newLog = {
            id: Date.now() + Math.random().toString(36).substring(2, 9),
            type,
            message,
            timestamp,
            data,
        };

        // Log ekle (başa)
        this._logs = [newLog, ...this._logs].slice(0, this._maxLogs);

        // Abonelere bildirim gönder
        this._notify(newLog);

        return newLog;
    }

    /**
     * Bilgi logu ekle (info)
     */
    info(message, data = null) {
        return this.add('info', message, data);
    }

    /**
     * Başarı logu ekle (success)
     */
    success(message, data = null) {
        return this.add('success', message, data);
    }

    /**
     * Uyarı logu ekle (warning)
     */
    warning(message, data = null) {
        return this.add('warning', message, data);
    }

    /**
     * Hata logu ekle (error)
     */
    error(message, data = null) {
        return this.add('error', message, data);
    }

    /**
     * Debug logu ekle (debug)
     */
    debug(message, data = null) {
        return this.add('debug', message, data);
    }

    /**
     * Tüm logları temizle
     */
    clear() {
        this._logs = [];
        this._notify({ type: 'clear' });
    }

    /**
     * Tüm logları getir
     */
    getAll() {
        return [...this._logs];
    }

    /**
     * Log değişikliklerini dinlemek için abone ol
     * @param {Function} callback - Her yeni log eklendiğinde çağrılacak fonksiyon
     * @returns {Function} Aboneliği iptal etmek için kullanılacak fonksiyon
     */
    subscribe(callback) {
        if (typeof callback !== 'function') {
            throw new Error('Callback bir fonksiyon olmalıdır');
        }

        this._subscribers.push(callback);

        // İlk çağrıda mevcut logları gönder
        callback(this.getAll());

        // Aboneliği iptal etme fonksiyonu
        return () => {
            this._subscribers = this._subscribers.filter((cb) => cb !== callback);
        };
    }

    /**
     * Tüm abonelere bildirim gönder
     * @private
     */
    _notify(log) {
        this._subscribers.forEach((callback) => {
            try {
                callback(log);
            } catch (error) {
                console.error('Log bildirim hatası:', error);
            }
        });
    }
}

// Singleton instance
const logger = new Logger();

export default logger;
